import { sql } from "drizzle-orm";
import { mutation } from "./reactive-mutation.js";
import type { MutationDef } from "./reactive-mutation-types.js";
import type {
  WorkflowDef,
  WorkflowDuration,
  WorkflowOptions,
  WorkflowStartResult,
} from "./workflow-types.js";
import { applyWorkflowStartAdmission, runWorkflowAdmissionCriticalSection } from "./workflow-admission.js";
import { workflowJsonb } from "./workflow-json.js";
import { WORKFLOW_WAKE_SIGNAL_CHANNEL, WORKFLOW_WAKE_SIGNAL_PAYLOAD } from "./workflow-wake-signal.js";
import { registerWorkflowsApi } from "./workflows-api.js";

declare global {
  // eslint-disable-next-line no-var
  var __gencow_workflowRegistry: Map<string, WorkflowDef<any, any>>;
}

const workflowRegistry = (globalThis.__gencow_workflowRegistry ??= new Map<string, WorkflowDef<any, any>>());

export const DEFAULT_WORKFLOW_MAX_DURATION_MS = 30 * 60 * 1000;
export const DEFAULT_WORKFLOW_MAX_RETRIES = 3;
export const WORKFLOW_RESUME_ACTION_PREFIX = "__gencow.workflow.resume";
export const WORKFLOW_REALTIME_KEY_PREFIX = "__gencow.workflow.state";

type SerializedWorkflowValue = { __gencowUndefined: true } | { value: unknown };

function isSerializedWorkflowValue(value: unknown): value is SerializedWorkflowValue {
  return !!value && typeof value === "object" && ("__gencowUndefined" in value || "value" in value);
}

export function serializeWorkflowValue(value: unknown): SerializedWorkflowValue {
  const payload = value === undefined ? { __gencowUndefined: true as const } : { value };

  try {
    return JSON.parse(JSON.stringify(payload)) as SerializedWorkflowValue;
  } catch (error) {
    const reason = error instanceof Error ? error.message : String(error);
    throw new Error(
      `workflow() only persists JSON-serializable values. Failed to serialize workflow payload: ${reason}`,
    );
  }
}

export function deserializeWorkflowValue(value: unknown): unknown {
  if (!isSerializedWorkflowValue(value)) return value;
  if ("__gencowUndefined" in value) return undefined;
  return value.value;
}

function clampRetries(retries: number | undefined): number {
  if (retries == null) return DEFAULT_WORKFLOW_MAX_RETRIES;
  if (!Number.isFinite(retries) || retries < 0) {
    throw new Error(`workflow() retries must be a non-negative finite number, got "${retries}"`);
  }
  return Math.floor(retries);
}

function parseDurationString(raw: string, label: string): number {
  const normalized = raw.trim().toLowerCase();
  const match = normalized.match(/^(\d+)(ms|s|m|h|d)$/);
  if (!match) {
    throw new Error(`${label} must be a number of ms or a string like "30m", "90s", "1h" — got "${raw}"`);
  }
  const value = Number(match[1]);
  const unit = match[2];
  const unitMs =
    unit === "ms" ? 1 : unit === "s" ? 1_000 : unit === "m" ? 60_000 : unit === "h" ? 3_600_000 : 86_400_000;
  return value * unitMs;
}

export function parseWorkflowDurationMs(raw: WorkflowDuration, label = "workflow duration"): number {
  if (typeof raw === "number") {
    if (!Number.isFinite(raw) || raw <= 0) {
      throw new Error(`${label} must be a positive finite number, got "${raw}"`);
    }
    return Math.floor(raw);
  }
  if (typeof raw !== "string") {
    throw new Error(
      `${label} must be a positive finite number or a string like "30m", "90s", "1h" — got "${String(raw)}"`,
    );
  }
  return parseDurationString(raw, label);
}

function normalizeMaxDurationMs(
  maxDuration: WorkflowDuration | undefined,
  label = "workflow() maxDuration",
): number {
  if (maxDuration == null) return DEFAULT_WORKFLOW_MAX_DURATION_MS;
  return parseWorkflowDurationMs(maxDuration, label);
}

function normalizeOptionalDurationMs(duration: WorkflowDuration | undefined, label: string): number | null {
  return duration == null ? null : parseWorkflowDurationMs(duration, label);
}

function normalizeConcurrency(concurrency: number | undefined): number | null {
  if (concurrency == null) return null;
  if (!Number.isFinite(concurrency) || concurrency <= 0) {
    throw new Error(`workflow() concurrency must be a positive finite number, got "${concurrency}"`);
  }
  return Math.floor(concurrency);
}

function isMissingWorkflowV2SchemaError(error: unknown): boolean {
  const code =
    error && typeof error === "object" && "code" in error ? String((error as { code?: unknown }).code) : "";
  const cause =
    error && typeof error === "object" && "cause" in error ? (error as { cause?: unknown }).cause : null;
  const causeCode =
    cause && typeof cause === "object" && "code" in cause ? String((cause as { code?: unknown }).code) : "";
  const message = error instanceof Error ? error.message : String(error);
  return (
    code === "42P01" ||
    code === "42703" ||
    causeCode === "42P01" ||
    causeCode === "42703" ||
    message.includes('relation "_gencow_workflow_runs_v2" does not exist') ||
    message.includes("relation _gencow_workflow_runs_v2 does not exist") ||
    message.includes('relation "_gencow_workflow_outbox_v2" does not exist') ||
    message.includes("relation _gencow_workflow_outbox_v2 does not exist") ||
    message.includes('column "max_active_duration_ms"') ||
    message.includes('column "retry_count"') ||
    message.includes('column "user_id"')
  );
}

function rows<T>(result: unknown): T[] {
  if (Array.isArray(result)) return result as T[];
  if (result && typeof result === "object" && Array.isArray((result as { rows?: unknown }).rows)) {
    return (result as { rows: T[] }).rows;
  }
  return [];
}

function numberFrom(value: unknown): number {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

async function hasWorkflowV2StartSchema(db: {
  execute: (query: ReturnType<typeof sql>) => Promise<unknown>;
}): Promise<boolean> {
  const runColumns = [
    "id",
    "workflow_name",
    "workflow_version",
    "args_json",
    "user_id",
    "max_active_duration_ms",
    "lifecycle_deadline_at",
    "retry_count",
    "max_retries",
    "max_attempts",
  ];
  const outboxColumns = ["id", "run_id", "kind", "available_at", "status"];
  const runColumnList = sql.join(
    runColumns.map((column) => sql`${column}`),
    sql`, `,
  );
  const outboxColumnList = sql.join(
    outboxColumns.map((column) => sql`${column}`),
    sql`, `,
  );
  const result = await db.execute(sql`
    SELECT
      (
        SELECT COUNT(*)::int
        FROM information_schema.columns
        WHERE table_schema = 'public'
          AND table_name = '_gencow_workflow_runs_v2'
          AND column_name IN (${runColumnList})
      ) AS run_column_count,
      (
        SELECT COUNT(*)::int
        FROM information_schema.columns
        WHERE table_schema = 'public'
          AND table_name = '_gencow_workflow_outbox_v2'
          AND column_name IN (${outboxColumnList})
      ) AS outbox_column_count
  `);
  const row = rows<{ run_column_count: unknown; outbox_column_count: unknown }>(result)[0];
  return (
    numberFrom(row?.run_column_count) === runColumns.length &&
    numberFrom(row?.outbox_column_count) === outboxColumns.length
  );
}

async function tryInsertWorkflowV2Run(options: {
  db: { execute: (query: ReturnType<typeof sql>) => Promise<unknown> };
  workflowId: string;
  workflowName: string;
  workflowVersion?: string | null;
  args: unknown;
  userId: string | null;
  maxActiveDurationMs: number;
  lifecycleTimeoutMs: number | null;
  maxRetries: number;
}): Promise<boolean> {
  try {
    if (!(await hasWorkflowV2StartSchema(options.db))) return false;
    await options.db.execute(sql`
      WITH inserted_run AS (
        INSERT INTO _gencow_workflow_runs_v2 (
          id,
          workflow_name,
          workflow_version,
          args_json,
          user_id,
          max_active_duration_ms,
          lifecycle_deadline_at,
          retry_count,
          max_retries,
          max_attempts
        )
        VALUES (
          ${options.workflowId},
          ${options.workflowName},
          ${options.workflowVersion ?? null},
          ${workflowJsonb(options.args)},
          ${options.userId},
          ${options.maxActiveDurationMs},
          CASE
            WHEN ${options.lifecycleTimeoutMs}::bigint IS NULL THEN NULL
            ELSE NOW() + (${options.lifecycleTimeoutMs}::bigint * INTERVAL '1 millisecond')
          END,
          0,
          ${options.maxRetries},
          ${options.maxRetries + 1}
        )
        RETURNING id
      ),
      inserted_outbox AS (
        INSERT INTO _gencow_workflow_outbox_v2 (
          id,
          run_id,
          kind,
          available_at,
          status
        )
        SELECT
          'start:' || inserted_run.id,
          inserted_run.id,
          'wake_run',
          NOW(),
          'pending'
        FROM inserted_run
        ON CONFLICT (id) DO NOTHING
        RETURNING id
      ),
      wake_notified AS (
        SELECT pg_notify(${WORKFLOW_WAKE_SIGNAL_CHANNEL}, ${WORKFLOW_WAKE_SIGNAL_PAYLOAD})
        FROM inserted_outbox
      )
      SELECT inserted_run.id
      FROM inserted_run
      LEFT JOIN wake_notified ON TRUE
    `);
    return true;
  } catch (error) {
    if (isMissingWorkflowV2SchemaError(error)) return false;
    throw error;
  }
}

async function tryDeleteWorkflowV2Run(
  db: { execute: (query: ReturnType<typeof sql>) => Promise<unknown> },
  workflowId: string,
): Promise<void> {
  try {
    await db.execute(sql`
      DELETE FROM _gencow_workflow_runs_v2
      WHERE id = ${workflowId}
    `);
  } catch (error) {
    if (!isMissingWorkflowV2SchemaError(error)) throw error;
  }
}

export function getWorkflowResumeActionName(name: string): string {
  return `${WORKFLOW_RESUME_ACTION_PREFIX}.${name}`;
}

export function createWorkflowRealtimeToken(): string {
  return crypto.randomUUID().replace(/-/g, "");
}

export function getWorkflowRealtimeKey(workflowId: string, realtimeToken: string): string {
  return `${WORKFLOW_REALTIME_KEY_PREFIX}.${workflowId}.${realtimeToken}`;
}

export function getWorkflowDef(name: string): WorkflowDef | undefined {
  return workflowRegistry.get(name);
}

export function getRegisteredWorkflows(): WorkflowDef[] {
  return Array.from(workflowRegistry.values());
}

/**
 * workflow() — durable multi-step execution with step memoization.
 *
 * The returned value is still a mutation definition, so existing API codegen and
 * frontend hooks keep working without extra workflow-specific tooling.
 */
export function workflow<TSchema = any, TReturn = any>(
  name: string,
  options: WorkflowOptions<TSchema, TReturn>,
): MutationDef<TSchema, WorkflowStartResult> {
  registerWorkflowsApi();

  const maxActiveDurationMs = normalizeMaxDurationMs(
    options.maxActiveDuration ?? options.maxDuration,
    options.maxActiveDuration == null ? "workflow() maxDuration" : "workflow() maxActiveDuration",
  );
  const lifecycleTimeoutMs = normalizeOptionalDurationMs(
    options.lifecycleTimeout,
    "workflow() lifecycleTimeout",
  );
  const maxRetries = clampRetries(options.retries);

  const def: WorkflowDef<TSchema, TReturn> = {
    name,
    argsSchema: options.args,
    isPublic: options.public === true,
    version: options.version,
    maxDurationMs: maxActiveDurationMs,
    maxActiveDurationMs,
    lifecycleTimeoutMs,
    concurrency: normalizeConcurrency(options.concurrency),
    maxRetries,
    handler: options.handler,
  };

  workflowRegistry.set(name, def);

  return mutation<TSchema, WorkflowStartResult>(name, {
    args: options.args,
    public: options.public,
    handler: async (ctx, args) => {
      const workflowId = crypto.randomUUID();
      const resumeAction = getWorkflowResumeActionName(name);
      const ownerId = ctx.auth.getUserIdentity()?.id ?? null;
      const persistedArgs = serializeWorkflowValue(args ?? {});
      const realtimeToken = createWorkflowRealtimeToken();
      let insertedWorkflowV2 = false;

      try {
        await runWorkflowAdmissionCriticalSection(ctx.unsafeDb, async (db) => {
          await applyWorkflowStartAdmission({
            db,
            workflowName: name,
            args: persistedArgs,
          });

          await db.execute(sql`
                    INSERT INTO _gencow_workflows (
                        id,
                        name,
                        args,
                        realtime_token,
                        status,
                        retry_count,
                        max_retries,
                        max_duration_ms,
                        user_id
                    )
                    VALUES (
                        ${workflowId},
                        ${name},
                        ${workflowJsonb(persistedArgs)},
                      ${realtimeToken},
                      'pending',
                      0,
                      ${maxRetries},
                      ${maxActiveDurationMs},
                      ${ownerId}
                    )
                `);

          insertedWorkflowV2 = await tryInsertWorkflowV2Run({
            db,
            workflowId,
            workflowName: name,
            workflowVersion: options.version ?? null,
            args: persistedArgs,
            userId: ownerId,
            maxActiveDurationMs,
            lifecycleTimeoutMs,
            maxRetries,
          });
        });
        const scheduledJobId = insertedWorkflowV2
          ? `start:${workflowId}`
          : ctx.scheduler.runAfter(0, resumeAction, { workflowId });
        return {
          id: workflowId,
          name,
          status: "pending",
          scheduledJobId,
        };
      } catch (error) {
        await ctx.unsafeDb.execute(sql`
                    DELETE FROM _gencow_workflows
                    WHERE id = ${workflowId}
                `);
        if (insertedWorkflowV2) {
          await tryDeleteWorkflowV2Run(ctx.unsafeDb, workflowId);
        }
        throw error;
      }
    },
  });
}
